import React from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import highchartsAccessibility from 'highcharts/modules/accessibility'
import monthlyReturnRates from './monthly-return-rate.json'

const getMonthlyReturn = (month, investmentBalance) => {
  let monthlyReturn
  switch (investmentBalance) {
    case 'fixedIncome':
      monthlyReturn = Number(monthlyReturnRates[month - 1].fixedIncome)
      break
    case 'conservative':
      monthlyReturn = Number(monthlyReturnRates[month - 1].conservative)
      break
    case 'moderatelyConservative':
      monthlyReturn = Number(monthlyReturnRates[month - 1].moderatelyConservative)
      break
    case 'moderate':
      monthlyReturn = Number(monthlyReturnRates[month - 1].moderate)
      break
    case 'moderatelyAggressive':
      monthlyReturn = Number(monthlyReturnRates[month - 1].moderatelyAggressive)
      break
    case 'aggressive':
      monthlyReturn = Number(monthlyReturnRates[month - 1].aggressive)
      break
    case 'equities':
      monthlyReturn = Number(monthlyReturnRates[month - 1].equities)
      break
    default:
      monthlyReturn = null
  }
  return monthlyReturn
}

const getFullName = investmentBalance => {
  let fullName
  switch (investmentBalance) {
    case 'fixedIncome':
      fullName = '100% Bonds'
      break
    case 'conservative':
      fullName = 'Conservative'
      break
    case 'moderatelyConservative':
      fullName = 'Moderately Conservative'
      break
    case 'moderate':
      fullName = 'Moderate'
      break
    case 'moderatelyAggressive':
      fullName = 'Moderately Aggressive'
      break
    case 'aggressive':
      fullName = 'Aggressive'
      break
    case 'equities':
      fullName = '100% Stocks'
      break
    default:
      fullName = null
  }
  return fullName
}

const getColor = investmentBalance => {
  let color
  switch (investmentBalance) {
    case 'fixedIncome':
      color = '#8EA71E' //apple green
      break
    case 'conservative':
      color = '#D17B00' //burnt orange
      break
    case 'moderatelyConservative':
      color = '#5D378C' //purple
      break
    case 'moderate':
      color = '#C02C83' //magenta
      break
    case 'moderatelyAggressive':
      color = '#7FCBC4' //light mint
      break
    case 'aggressive':
      color = '#2AA7E1' //sky blue
      break
    case 'equities':
      color = '#FDD835' // yellow
      break
    default:
      color = null
  }
  return color
}

const calculateSeriesData = (
  initialBalance,
  interestAdjustmentRate,
  contributionRate,
  investmentBalance
) => {
  let balance = initialBalance
  let interestAdjustment = 1
  let dataPoints = [balance]

  for (let i = 0; i < 273; i++) {
    // increase interest adjustment by interestAdjustmentRate every 12 months
    if (i % 12 === 0 && i !== 0) {
      interestAdjustment = interestAdjustment * (1 + interestAdjustmentRate / 100)
    }

    // calculate contribution
    let contribution = ((initialBalance * contributionRate) / 100 / 12) * interestAdjustment

    // calculate new balance
    balance = balance * (1 + getMonthlyReturn(i + 1, investmentBalance) / 100) + contribution
    dataPoints.push(balance)
  }
  return dataPoints
}

const createOptions = (
  initialBalance,
  interestAdjustmentRate,
  contributionRate,
  investmentBalanceArray,
  graphHeading,
  dataProvided
) => {
  let seriesArray = []
  if (dataProvided) {
    investmentBalanceArray.forEach(investmentBalance => {
      seriesArray.push({
        name: getFullName(investmentBalance),
        data: calculateSeriesData(
          initialBalance,
          interestAdjustmentRate,
          contributionRate,
          investmentBalance
        ),
        color: getColor(investmentBalance),
        animation: {
          duration: 2000,
        },
        visible: true,
      })
    })
  }

  return {
    chart: {
      height: '100%',
      animation: {
        duration: 1000,
      },
      className: 'market-volatility-graph',
    },

    title: {
      useHTML: true,
      margin: 65,
      text: `${graphHeading}`,
    },

    tooltip: {
      enabled: false,
    },

    showAxes: true,

    yAxis: {
      title: {
        useHTML: true,
        text: '<span class="download-y-axis u-textPrimary u-textSizeMsr yAxis">Account Value</span>',
      },
      min: 0,
      showEmpty: true,
    },

    xAxis: {
      accessibility: {
        rangeDescription: 'Range: 2000 to 2022',
      },
      showEmpty: true,
      type: 'datetime',
      title: {
        useHTML: true,
        text: '<span class="download-x-axis u-textPrimary u-textSizeMsr">Year</span>',
      },
    },

    legend: {
      layout: 'horizontal',
      verticalAlign: 'top',
      align: 'center',
      enabled: dataProvided,
    },

    plotOptions: {
      line: {
        enableMouseTracking: false,
      },
      series: {
        pointStart: Date.UTC(2000, 0, 1),

        // if data is provided, each data point is for one month (in milliseconds)
        // if data is not provided, we supply two points
        // that span 20 years (in milliseconds) to set the scale properly
        pointInterval: dataProvided ? 24 * 3600 * 1000 * 30 : 24 * 3600 * 1000 * 30 * 12 * 20,
      },
    },
    series: dataProvided
      ? seriesArray
      : [
          {
            name: '',
            data: [0, 1000000],
            color: 'transparent',
            animation: {
              duration: 2000,
            },
            visible: true,
          },
        ],
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            legend: {
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'bottom',
            },
          },
        },
      ],
    },
  }
}

highchartsAccessibility(Highcharts)

export default function MarketVolatilityGraph(props) {
  return (
    <div>
      <HighchartsReact
        highcharts={Highcharts}
        options={
          props.submitted
            ? createOptions(
                props.initialBalance,
                props.interestAdjustmentRate,
                props.contributionRate,
                props.investmentBalanceArray,
                props.graphHeading,
                true
              )
            : createOptions(null, null, null, null, props.graphHeading, false)
        }
      />
    </div>
  )
}
